import React from "react";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import { FOOTER_NAV } from "./navigation";
import { COMPANY } from "../../data/company";
import Logo from "./Logo";
import { Label, Rule } from "../ui/Type";
import { color, font, motion, space, type } from "../../design/tokens";

const LinkColumn = ({ title, links }) => (
  <Box component="nav" aria-label={title}>
    <Label tone="onInk" bare sx={{ marginBottom: 2.5 }}>
      {title}
    </Label>
    <Box component="ul" sx={{ listStyle: "none", margin: 0, padding: 0, display: "grid" }}>
      {links.map((link) => (
        <Box component="li" key={link.to}>
          <Box
            component={Link}
            to={link.to}
            sx={{
              ...type.bodySm,
              color: color.onInkMute,
              // Padding rather than list `gap`, so the tap target itself grows
              // past the WCAG 2.2 24px minimum instead of just the space
              // between targets. A 15px line on its own measures ~21px.
              display: "block",
              paddingBlock: "6px",
              transition: `color ${motion.base} ${motion.ease}`,
              "&:hover": { color: color.onInk },
            }}
          >
            {link.label}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const SiteFooter = () => {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: color.ink,
        color: color.onInk,
        paddingTop: { xs: 8, md: 12 },
        paddingBottom: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: space.maxWidth,
          marginInline: "auto",
          paddingInline: space.gutter,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "1.4fr repeat(3, 1fr) 1.2fr",
            },
            gap: { xs: 6, lg: space.gridGap },
          }}
        >
          <Box>
            <Logo stacked />
            <Box
              component="p"
              sx={{ ...type.bodySm, color: color.onInkMute, marginTop: 3, maxWidth: 300 }}
            >
              A renovation contractor based in {COMPANY.address.locality}, working
              across Durham Region. Kitchens, bathrooms, legal basements, flooring,
              tile, drywall and painting — mostly self-performed, start to finish.
            </Box>

            <Box sx={{ display: "flex", gap: 3, marginTop: 3.5, marginLeft: "-8px" }}>
              {COMPANY.social.map((channel) => (
                <Box
                  key={channel.label}
                  component="a"
                  href={channel.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...type.label,
                    color: color.onInkMute,
                    // An 11px label is a 11px tap target without this. Negative
                    // margin on the row keeps the visual left edge aligned.
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: 28,
                    paddingInline: "8px",
                    transition: `color ${motion.base} ${motion.ease}`,
                    "&:hover": { color: color.clayLight },
                  }}
                >
                  {channel.label}
                </Box>
              ))}
            </Box>
          </Box>

          <LinkColumn title="Company" links={FOOTER_NAV.company} />
          <LinkColumn title="Services" links={FOOTER_NAV.services} />
          <LinkColumn title="Service Areas" links={FOOTER_NAV.areas} />

          <Box>
            <Label tone="onInk" bare sx={{ marginBottom: 2.5 }}>
              Get in touch
            </Label>
            <Box sx={{ display: "grid", gap: 1.75 }}>
              <Box
                component="a"
                href={`tel:${COMPANY.phone.primaryE164}`}
                sx={{
                  fontFamily: font.display,
                  fontSize: "1.5rem",
                  lineHeight: 1.1,
                  color: color.onInk,
                  transition: `color ${motion.base} ${motion.ease}`,
                  "&:hover": { color: color.clayLight },
                }}
              >
                {COMPANY.phone.primary}
              </Box>
              <Box
                component="a"
                href={`tel:${COMPANY.phone.secondaryE164}`}
                sx={{
                  ...type.bodySm,
                  color: color.onInkMute,
                  "&:hover": { color: color.onInk },
                }}
              >
                {COMPANY.phone.secondary}
              </Box>
              <Box
                component="a"
                href={`mailto:${COMPANY.email}`}
                sx={{
                  ...type.bodySm,
                  color: color.onInkMute,
                  wordBreak: "break-word",
                  "&:hover": { color: color.onInk },
                }}
              >
                {COMPANY.email}
              </Box>
              <Box component="p" sx={{ ...type.bodySm, color: color.onInkMute, margin: 0 }}>
                {COMPANY.address.display}
              </Box>
            </Box>
          </Box>
        </Box>

        <Rule tone="onInk" sx={{ marginTop: { xs: 7, md: 10 }, marginBottom: 3 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Box component="p" sx={{ ...type.caption, color: color.onInkMute, margin: 0 }}>
              © {year} {COMPANY.name}. {COMPANY.tagline}
            </Box>
            <Box
              component="span"
              aria-hidden="true"
              sx={{ ...type.caption, color: color.lineOnInk, display: { xs: "none", sm: "block" } }}
            >
              /
            </Box>
            <Box
              component="a"
              href="https://autorobos.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...type.caption,
                color: color.onInkMute,
                display: "inline-flex",
                alignItems: "center",
                minHeight: 26,
                transition: `color ${motion.base} ${motion.ease}`,
                "&:hover": { color: color.clayLight },
              }}
            >
              Design by Autorobos
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{
              ...type.label,
              color: color.onInkMute,
              background: "none",
              border: "none",
              paddingBlock: "9px",
              paddingInline: 0,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              transition: `color ${motion.base} ${motion.ease}`,
              "&:hover": { color: color.onInk },
            }}
          >
            Back to top ↑
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SiteFooter;
